import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
function Background({ children, disableLoadingAnimation = false }) {
    const [isVideoLoaded, setIsVideoLoaded] = useState(disableLoadingAnimation);
    const [videoError, setVideoError] = useState(null);
    const videoRef = useRef(null);
    const mountTimeRef = useRef(Date.now());
    useEffect(() => {
        mountTimeRef.current = Date.now();
        if (disableLoadingAnimation) {
            setIsVideoLoaded(true);
        }
    }, [disableLoadingAnimation]);
    useEffect(() => {
        // Try to play video on first user interaction if autoplay fails
        const video = videoRef.current;
        if (!video)
            return;
        const tryPlay = async () => {
            try {
                if (video.paused) {
                    await video.play();
                    // Clear any autoplay errors if play succeeds
                    setVideoError(null);
                }
            }
            catch (error) {
                // Silently handle - autoplay might be blocked, but that's okay
                // The video will still load and can be played manually if needed
            }
        };
        // Check if video is paused after a short delay (autoplay might have failed)
        const checkAutoplay = setTimeout(() => {
            if (video.paused && video.readyState >= 2) {
                // Video loaded but not playing - try on first user interaction
                const events = ['touchstart', 'touchend', 'click', 'scroll'];
                const playOnInteraction = () => {
                    tryPlay();
                    events.forEach(event => {
                        document.removeEventListener(event, playOnInteraction);
                    });
                };
                events.forEach(event => {
                    document.addEventListener(event, playOnInteraction, { once: true, passive: true });
                });
            }
        }, 1000);
        return () => {
            clearTimeout(checkAutoplay);
        };
    }, []);
    const handleVideoCanPlay = () => {
        if (disableLoadingAnimation) {
            return;
        }
        const elapsed = Date.now() - mountTimeRef.current;
        const minDelay = 500; // Minimum 500ms delay
        if (elapsed >= minDelay) {
            setIsVideoLoaded(true);
        }
        else {
            const remainingDelay = minDelay - elapsed;
            setTimeout(() => {
                setIsVideoLoaded(true);
            }, remainingDelay);
        }
    };
    const handleVideoError = (e) => {
        const video = e.currentTarget;
        const error = video.error;
        let errorMessage = 'Video failed to load';
        if (error) {
            switch (error.code) {
                case error.MEDIA_ERR_ABORTED:
                    errorMessage = 'Video loading aborted';
                    break;
                case error.MEDIA_ERR_NETWORK:
                    errorMessage = 'Network error loading video';
                    break;
                case error.MEDIA_ERR_DECODE:
                    errorMessage = 'Video decode error (format not supported?)';
                    break;
                case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    errorMessage = 'Video format not supported';
                    break;
                default:
                    errorMessage = `Video error (code: ${error.code})`;
            }
        }
        console.error('Video failed to load:', errorMessage, e);
        setVideoError(errorMessage);
        // Still show content even if video fails
        setIsVideoLoaded(true);
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: -1,
                } }, { children: [_jsx("video", Object.assign({ ref: videoRef, autoPlay: true, loop: true, muted: true, playsInline: true, preload: "auto", onCanPlay: handleVideoCanPlay, onError: handleVideoError, onLoadedData: () => {
                            // Fallback: ensure video is marked as loaded when data is loaded
                            if (!disableLoadingAnimation && !isVideoLoaded) {
                                handleVideoCanPlay();
                            }
                        }, style: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: -1,
                        } }, { children: _jsx("source", { src: "/nature-montage-bw-looped.webm", type: "video/webm" }) })), _jsx("div", { style: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            zIndex: 0,
                        } })] })), !isVideoLoaded && (_jsx("div", Object.assign({ style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 1.0)',
                    zIndex: 1000,
                } }, { children: _jsx("img", { src: "/crownwell-partners-logo-v2-alt.png", alt: "Crownwell Partners Logo", style: {
                        height: '80px',
                        width: 'auto',
                        opacity: 0.9,
                    } }) }))), videoError && (_jsxs("div", Object.assign({ style: {
                    position: 'fixed',
                    top: '10px',
                    left: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(255, 0, 0, 0.9)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    zIndex: 10000,
                    fontFamily: 'Arial, sans-serif',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                } }, { children: [_jsx("strong", { children: "Video Error:" }), " ", videoError] }))), children && (_jsx("div", Object.assign({ style: { position: 'relative', zIndex: 1, width: '100%' } }, { children: children })))] }));
}
export default Background;
